import { Controller } from "@hotwired/stimulus"

// Empêche de décocher la dernière case cochée d'un groupe (#2236 étape 03 : "au
// moins une compétition doit rester sélectionnée par sport", même règle pour les
// sports à l'étape 02). La case reste cochée mais grisée tant qu'elle est seule —
// dès qu'une autre est recochée, elle redevient normale.
//
// Jamais `disabled` : un input désactivé n'est PAS soumis avec le formulaire, donc
// désactiver la dernière case cochée l'aurait fait disparaître de `sport_keys[]` /
// `competition_ids[]` — envoyant exactement le tableau vide que cette garde doit
// empêcher. On annule le `click` à la place — juste une affordance : le serveur
// revalide dans tous les cas (cf. Dashboard::OnboardingController#update_sports
// et #update_competitions).
//
// Mode opt-in (#2801, étape sports) : avec une cible `submit`, le groupe peut être
// vide. Rien n'est verrouillé ; c'est le CTA qui est inactif tant qu'aucune case
// n'est cochée (un verrou sur la première case cochée empêcherait de se raviser).
export default class extends Controller {
  static targets = ["checkbox", "submit"]

  connect() {
    this.refresh()
  }

  // ATTENTION à l'ordre des évènements : pour une case à cocher, le navigateur
  // bascule `checked` dans les "pre-click activation steps", AVANT de dispatcher
  // `click`. Donc ici `box.checked` porte déjà l'état VOULU, pas l'ancien, et
  // `preventDefault()` restaure l'état précédent (et `change` ne part jamais).
  //
  // La version précédente testait `box.checked && checkedCount === 1`, ce qui
  // lisait l'état à l'envers : décocher la dernière case passait (au clic elle
  // valait déjà false), et cocher une case alors qu'aucune ne l'était était
  // bloqué (elle valait true et était la seule) — l'utilisateur se retrouvait
  // avec zéro case cochée et l'impossibilité d'en recocher une.
  guard(event) {
    if (this.hasSubmitTarget) return

    const box = event.currentTarget
    if (box.checked) return // cocher est toujours autorisé

    // `box` vient d'être décochée : s'il ne reste plus rien, on annule.
    if (this.#checked().length === 0) {
      event.preventDefault()
      // Le clic refusé ne doit pas atteindre l'écouteur haptique du document :
      // vibrer `selection` confirmerait physiquement un changement qui n'a pas eu lieu.
      // Les autres écouteurs de la case (track) tournent quand même.
      event.stopPropagation()
    }
  }

  refresh() {
    const checked = this.#checked()
    if (this.hasSubmitTarget) return this.#refreshSubmit(checked.length === 0)

    const lone = checked.length === 1 ? checked[0] : null

    // L'état "sélectionné" (bordure lime, icône/pastille teintées) est rendu en
    // pur CSS via `has-[:checked]` / `group-has-[:checked]` (maquettes #2235
    // Sports/Competitions/Basket) — il se resynchronise seul au clic, sans JS.
    // Seule la case restante SEULE ne peut pas se déduire du CSS (ça dépend des
    // AUTRES cases du groupe) : on la grise pour signaler qu'elle est verrouillée.
    this.checkboxTargets.forEach(box => {
      const label = box.closest("label")
      if (!label) return

      label.classList.toggle("opacity-60", box === lone)
      // `aria-disabled` plutôt que `disabled` : la case reste soumise (cf.
      // commentaire de tête), mais un lecteur d'écran annonce le verrou au lieu
      // de laisser croire à une case ordinaire qui ne réagit pas.
      box.setAttribute("aria-disabled", box === lone ? "true" : "false")
    })
  }

  // `disabled` suffit ici : contrairement à une case, un bouton désactivé n'a aucune
  // valeur à soumettre. Les classes reprennent celles de FooterButtonComponent#disabled.
  #refreshSubmit(empty) {
    this.submitTargets.forEach(button => {
      button.disabled = empty
      button.setAttribute("aria-disabled", String(empty))
      button.classList.toggle("opacity-40", empty)
      button.classList.toggle("cursor-not-allowed", empty)
    })
  }

  #checked() {
    return this.checkboxTargets.filter(box => box.checked)
  }
}
